import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Auth_APIs from '../../utilities/api/auth_api';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getUserSuccess, getUserFail } from '../../redux/slices/userSlice';
import { toast, ToastContainer } from 'react-toastify';
import expandleft from '../../utilities/dummy_assets/Expand_left.png';
import { Link, IconButton } from '@mui/material';
import background from '../../utilities/dummy_assets/background.png';
import Logos from '../../components/logos';

const typographyStyle = {
  color: 'var(--Primary-Primary03, #5C5C5C)',
  fontFamily: 'Inter',
  fontSize: '0.875rem',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: 'normal',
  letterSpacing: '-0.01375rem',
  marginBottom: '0.25rem',
};

const boxStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '.6rem',
  alignSelf: 'stretch',
};

const styledTextField = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '& fieldset': {
      border: '1.5px solid #B5B5B5', // Set custom border
    },
    '&:hover fieldset': {
      borderColor: '#007BFF', // Border color on hover
    },
    '&.Mui-focused fieldset': {
      borderColor: '#007BFF', // Border color when focused
    },
  },
};

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Initialize state with defensive checks
  const [userData, setUserData] = useState(() => ({
    email: '',
    //password: "",
  }));
  // Using a ref for password so it's not stored in React state
  const passwordRef = React.useRef();

  const [isChecked, setIsChecked] = useState(() => false);
  const [errors, setErrors] = useState(() => ({
    email: '',
    password: '',
    terms: '',
  }));
  const [isSubmitted, setIsSubmitted] = useState(() => false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return 'Please enter a valid email';
        return '';
      case 'password':
        if (!value.trim()) return 'Password is required';
        if (value.length < 6) return 'Password must be at least 6 characters';
        return '';
      case 'terms':
        return !isChecked ? 'You must accept the terms' : '';
      default:
        return '';
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;

    setUserData(prevData => ({ ...prevData, [name]: value }));

    // Only validate if form was already submitted
    if (isSubmitted) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleCheckboxChange = e => {
    const checked = e.target.checked;
    setIsChecked(checked);

    // Only validate if form was already submitted
    if (isSubmitted) {
      const error = checked ? '' : 'You must accept the terms';
      setErrors(prev => ({ ...prev, terms: error }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      email: validateField('email', userData.email),
      password: validateField('password', passwordRef.current?.value || ''),
      // terms: !isChecked ? 'You must accept the terms' : ''
    };
    setErrors(newErrors);

    return !Object.values(newErrors).some(error => error);
  };

  const handleClick = async () => {
    setIsSubmitted(true);
    if (!validateForm()) {
      return;
    }
    setIsLoading(true);

    Auth_APIs.adminLogin({
      email: userData.email,
      password: passwordRef.current.value,
    })
      .then(response => {
        if (response && response.password_change_required) {
          toast.info(
            'Password change required for first login. Redirecting to reset password page...'
          );

          // Navigate to reset password page with email and user data as state
          navigate('/reset_password', {
            state: {
              email: userData.email,
              user_id: response.user_id,
              isFirstLogin: true,
            },
          });
          return;
        }

        // Normal login flow
        dispatch(getUserSuccess(response));
        toast.success('Login successful!');
        navigate('/admin_dashboard');
      })
      .catch(error => {
        console.log(error);
        dispatch(getUserFail(error.message));
        toast.error('Login failed. Please check your credentials.');
      })
      .finally(() => {
        setIsLoading(false);
        // clear password field after login
        if (passwordRef.current) passwordRef.current.value = '';
      });
  };

  //const { email, password } = userData;
  const { email } = userData;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        flexGrow: 1,
        backgroundColor: { xs: 'white', sm: '#F7F7F7' },
        backgroundImage: { xs: 'none', sm: `url(${background})` },
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Logo in the Top Left */}
      {/* <Box
                sx={{
                    position: "absolute",
                    top: "12px",
                    left: "50px",
                    display: "flex",
                    alignItems: "center"
                }}
            >
                <img src={Logo} alt="MBF Logo" style={{ height: '69px', width: '159px' }} />
            </Box> */}

      {/* "Go to User Site" link */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          p: '16px 24px',
        }}
      >
        <span
          onClick={() =>
            (window.location.href = 'https://tinkerspace.mis.mapit.ai/login')
          }
          style={{
            color: 'var(--Accent-Accent-01, #0062CC)',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          Go to Student Site
        </span>
      </Box>

      {/* Centered Container for Form */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          justifyContent: 'center', // Center content vertically
          alignItems: 'center', // Center content horizontally
          padding: '1.5rem',
        }}
      >
        {/* Form Section with White Background */}
        <Box
          sx={{
            //     display: "flex",
            // flexDirection: "column",
            // flexGrow: 1,
            // justifyContent: "center",  // Center content vertically
            // alignItems: "center",      // Center content horizontally
            width: '100%',
            maxWidth: '26rem',
            padding: { xs: '1rem', sm: '2rem' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
            textAlign: 'center',
            backgroundColor: 'white',
            borderRadius: { xs: '16px', sm: '24px' },
            boxShadow: { xs: 'none', sm: '0 4px 6px rgba(0, 0, 0, 0.1)' },
          }}
        >
          <Box sx={boxStyle}>
            <Typography sx={typographyStyle}>
              Email Address{' '}
              <span style={{ color: '#FF4D4F', marginLeft: '2px' }}>*</span>
            </Typography>

            <TextField
              sx={{
                ...styledTextField,
                '& .MuiOutlinedInput-root': {
                  ...styledTextField['& .MuiOutlinedInput-root'],
                  '& fieldset': {
                    borderColor:
                      isSubmitted && errors.email ? '#FF4D4F' : '#B5B5B5',
                  },
                },
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  passwordRef.current?.focus();
                }
              }}
              name="email"
              value={email}
              onChange={handleChange}
              placeholder="Enter email address"
              variant="outlined"
              error={isSubmitted && !!errors.email}
              helperText={isSubmitted ? errors.email : ''}
              FormHelperTextProps={{
                style: {
                  marginLeft: 0,
                  color: '#FF4D4F',
                  fontSize: '0.75rem',
                  marginTop: '4px',
                },
              }}
            />
          </Box>

          <Box sx={boxStyle}>
            <Typography sx={typographyStyle}>
              Password{' '}
              <span style={{ color: '#FF4D4F', marginLeft: '2px' }}>*</span>
            </Typography>

            <TextField
              type={showPassword ? 'text' : 'password'}
              required
              sx={{
                ...styledTextField,
                '& .MuiOutlinedInput-root': {
                  ...styledTextField['& .MuiOutlinedInput-root'],
                  '& fieldset': {
                    borderColor:
                      isSubmitted && errors.password ? '#FF4D4F' : '#B5B5B5',
                  },
                },
              }}
              name="password"
              inputRef={passwordRef}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleClick();
                }
              }}
              onChange={handleChange}
              placeholder="Enter password"
              variant="outlined"
              error={isSubmitted && !!errors.password}
              helperText={isSubmitted ? errors.password : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: '#666' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              FormHelperTextProps={{
                style: {
                  marginLeft: 0,
                  color: '#FF4D4F',
                  fontSize: '0.75rem',
                  marginTop: '4px',
                },
              }}
            />
          </Box>

          {/* Checkbox and Forgot Password on same line */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              alignSelf: 'stretch',
              mt: '-8px',
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontFamily: 'Inter',
                fontWeight: 400,
                letterSpacing: '-0.01375rem',
                color: 'var(--Accent-Accent-01, #007BFF)',
                cursor: 'pointer',
                mb: '10px',
              }}
              onClick={() => navigate('/forgot_password')}
            >
              Forgot Password?
            </Typography>
            {/* <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isChecked}
                                        onChange={handleCheckboxChange}
                                        sx={{
                                            color: isSubmitted && errors.terms ? '#FF4D4F' : "var(--Accent-Accent-01, #007BFF)",
                                            '&.Mui-checked': {
                                                color: "var(--Accent-Accent-01, #007BFF)",
                                            },
                                            '&.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.26)',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontFamily: "Inter",
                                            color: isSubmitted && errors.terms ? '#FF4D4F' : "var(--Primary-Primary03, #5C5C5C)",
                                            lineHeight: '1.2',
                                        }}
                                    >
                                        By using the MBF MIS,you consent to the processing of your information.
                                        {isSubmitted && errors.terms && (
                                            <Typography
                                                component="span"
                                                sx={{
                                                    display: 'block',
                                                    color: '#FF4D4F',
                                                    fontSize: '0.75rem',
                                                    marginTop: '2px',
                                                }}
                                            >
                                                {errors.terms}
                                            </Typography>
                                        )}
                                    </Typography>
                                }
                                sx={{ margin: 0 }}
                            />
                        </Box> */}
          </Box>

          {/* Login Button */}
          <Button
            onClick={handleClick}
            disabled={
              isLoading ||
              !email ||
              !passwordRef.current?.value ||
              Object.values(errors).some(error => error)
            }
            sx={{
              padding: '1rem',
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: 'stretch',
              borderRadius: '12px',
              background: 'var(--Accent-Accent-00, #007BFF)',
              color: '#FFF',
              '&:hover': {
                background: '#0056b3',
              },
              '&.Mui-disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: '#FFF' }} />
            ) : (
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: '#FFF',
                }}
              >
                Login as Admin
              </Typography>
            )}
          </Button>

          {/* Back and Register Links */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              alignSelf: 'stretch',
              mt: '0.5rem',
            }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <img
                src={expandleft}
                alt="Back"
                onClick={() => navigate('/login')}
                style={{ marginRight: '8px' }}
              />
              <Typography
                onClick={() => navigate('/login')}
                sx={{
                  fontSize: '1rem',
                  color: 'var(--Accent-Accent-00, #007BFF)',
                  cursor: 'pointer',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Back
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '1rem',
                color: 'var(--Accent-Accent-01, #3F3F3F)',
              }}
            >
              Don’t have an account?{' '}
              <span
                onClick={() => navigate('/register-instructions')}
                style={{
                  color: 'var(--Accent-Accent-01, #007BFF)',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Register here
              </span>
            </Typography>
          </Box>
          <Box
            sx={{
              alignSelf: 'center',
              display: 'flex',
              gap: '1rem',
              marginTop: '1rem',
            }}
          >
            {/* LinkedIn */}
            <Link
              href="https://www.linkedin.com/company/maker-bhavan-foundation/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: '#0077B5', '&:hover': { opacity: 0.8 } }}
            >
              <IconButton sx={{ padding: '4px' }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="#0077B5">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </IconButton>
            </Link>

            {/* YouTube */}
            <Link
              href="https://www.youtube.com/@Makerbhavanfoundation"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: '#FF0000', '&:hover': { opacity: 0.8 } }}
            >
              <IconButton sx={{ padding: '4px' }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="red">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </IconButton>
            </Link>

            {/* Instagram */}
            <Link
              href="https://www.instagram.com/makerbhavan_india/?igsh=dWRhN3djbzM5cnNi"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: '#E4405F', '&:hover': { opacity: 0.8 } }}
            >
              <IconButton sx={{ padding: '4px' }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="#E4405F">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </IconButton>
            </Link>
          </Box>
        </Box>
      </Box>

      {/* Footer with "Powered by" */}
      <Logos />
    </Box>
  );
};

export default AdminLoginPage;
